"use client";
/**
 * Phase 1 infrastructure for the calculators page.
 *
 * CalcSettingsContext persists a handful of defaults to localStorage so every
 * calculator on /authority/calculators can read the user's chosen unit system,
 * default dehumidifier, etc., without re-typing them on every screen.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export type UnitSystem = "us" | "metric";

export type CalcSettings = {
  units: UnitSystem;
  defaultDehuId: string;
  defaultLinearFt: number;
  defaultHeightFt: number;
  defaultEmail: string;
};

const DEFAULTS: CalcSettings = {
  units: "us",
  defaultDehuId: "dri-eaz-lgr-7000xli",
  defaultLinearFt: 10,
  defaultHeightFt: 8,
  defaultEmail: "",
};

const STORAGE_KEY = "drr.calc.settings.v1";

type Ctx = {
  settings: CalcSettings;
  update: (patch: Partial<CalcSettings>) => void;
  reset: () => void;
};

const CalcSettingsContext = createContext<Ctx | null>(null);

export function CalcSettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<CalcSettings>(DEFAULTS);
  const [hydrated, setHydrated] = useState(false);

  // Hydrate from localStorage once after mount.
  useEffect(() => {
    try {
      const raw = typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY) : null;
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<CalcSettings>;
        setSettings({ ...DEFAULTS, ...parsed });
      }
    } catch {
      // ignore malformed storage
    } finally {
      setHydrated(true);
    }
  }, []);

  // Persist on every change, but not during the initial render before hydration.
  useEffect(() => {
    if (!hydrated) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // quota / private mode: fine to ignore
    }
  }, [settings, hydrated]);

  const update = useCallback((patch: Partial<CalcSettings>) => {
    setSettings((prev) => ({ ...prev, ...patch }));
  }, []);

  const reset = useCallback(() => setSettings(DEFAULTS), []);

  const value = useMemo<Ctx>(() => ({ settings, update, reset }), [settings, update, reset]);

  return <CalcSettingsContext.Provider value={value}>{children}</CalcSettingsContext.Provider>;
}

export function useCalcSettings(): Ctx {
  const ctx = useContext(CalcSettingsContext);
  if (!ctx) {
    throw new Error("useCalcSettings must be used inside <CalcSettingsProvider>");
  }
  return ctx;
}

/** Convert ft ↔ m depending on active units. Input is always stored in feet. */
export function displayLength(ft: number, units: UnitSystem, digits = 1): string {
  if (units === "metric") return `${(ft * 0.3048).toFixed(digits)} m`;
  return `${ft.toFixed(digits)} ft`;
}

/** Convert ft² ↔ m² */
export function displayArea(sqft: number, units: UnitSystem, digits = 0): string {
  if (units === "metric") return `${(sqft * 0.092903).toFixed(digits)} m²`;
  return `${sqft.toFixed(digits)} ft²`;
}

/** Convert ft³ ↔ m³ */
export function displayVolume(cuft: number, units: UnitSystem, digits = 0): string {
  if (units === "metric") return `${(cuft * 0.0283168).toFixed(digits)} m³`;
  return `${cuft.toFixed(digits)} ft³`;
}

/** Convert gallons(US) ↔ litres */
export function displayGallons(galUS: number, units: UnitSystem, digits = 1): string {
  if (units === "metric") return `${(galUS * 3.78541).toFixed(digits)} L`;
  return `${galUS.toFixed(digits)} gal`;
}

/** Convert °F ↔ °C */
export function displayTemp(f: number, units: UnitSystem, digits = 1): string {
  if (units === "metric") return `${(((f - 32) * 5) / 9).toFixed(digits)} °C`;
  return `${f.toFixed(digits)} °F`;
}
